import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as React from "react";
import { useFormatAmount } from "../../../shared";
import { DashboardChartCard } from "./DashboardChartCard";
import { useChartColors } from "./chartHelpers";

const tooltipSx = {
  backgroundColor: "background.paper",
  color: "text.primary",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.12)",
  p: "8px 10px",
  maxWidth: 260,
};

// text stays in ink; the series colour sits beside it as a dot, like the chart tooltips
const MetricRow = ({ color, ink, label, value }) => (
  <Stack direction="row" alignItems="center" gap="6px">
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: color,
        flex: "none",
      }}
    />
    <Typography variant="caption" fontWeight={600} sx={{ color: ink, lineHeight: 1.6 }}>
      {label}: {value}
    </Typography>
  </Stack>
);

const StoreMetrics = ({ item }) => {
  const formatAmount = useFormatAmount();
  const colors = useChartColors();
  return (
    <Stack gap="2px">
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color: colors.title, lineHeight: 1.6, mb: "2px" }}
      >
        {item.store_name}
      </Typography>
      <MetricRow
        color={colors.gross}
        ink={colors.label}
        label="Gross Sales"
        value={formatAmount(item.gross_total)}
      />
      <MetricRow
        color={colors.net}
        ink={colors.label}
        label="Net Sales"
        value={formatAmount(item.net_total)}
      />
      <MetricRow
        color={colors.atv}
        ink={colors.label}
        label="ATV"
        value={formatAmount(item.atv)}
      />
      <MetricRow
        color={colors.transactions}
        ink={colors.label}
        label="Transactions"
        value={Number(item.transaction_count || 0).toLocaleString()}
      />
    </Stack>
  );
};

export const DashboardStoreSalesCard = ({ title, data = [] }) => {
  const formatAmount = useFormatAmount();
  const chartColors = useChartColors();
  const [viewMode, setViewMode] = React.useState("graph");
  const [sortConfig, setSortConfig] = React.useState({
    key: "gross_total",
    direction: "desc",
  });
  const sortLabelSx = {
    "& .MuiSvgIcon-root": {
      height: "15px",
      width: "15px",
    },
    whiteSpace: "nowrap",
  };

  // bars read best largest first
  const sortedByGross = React.useMemo(
    () =>
      [...data].sort(
        (a, b) => Number(b.gross_total || 0) - Number(a.gross_total || 0)
      ),
    [data]
  );
  const maxGross = sortedByGross.reduce(
    (max, item) => Math.max(max, Number(item.gross_total || 0)),
    0
  );

  const handleViewMode = (_, value) => {
    if (value) {
      setViewMode(value);
    }
  };

  const handleSort = (key) => {
    setSortConfig((previousState) => {
      if (previousState.key === key) {
        return {
          key,
          direction: previousState.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedTableData = React.useMemo(() => {
    if (!sortConfig.key) {
      return data;
    }
    const sortedData = [...data].sort((a, b) => {
      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];
      // amounts come from the API as numeric strings, so only store_name sorts as text
      if (sortConfig.key === "store_name") {
        return String(valueA || "").localeCompare(String(valueB || ""));
      }
      return Number(valueA || 0) - Number(valueB || 0);
    });

    if (sortConfig.direction === "desc") {
      sortedData.reverse();
    }
    return sortedData;
  }, [data, sortConfig]);

  return (
    <DashboardChartCard
      title={title}
      isEmpty={!data.length}
      action={
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewMode}
          size="small"
        >
          <ToggleButton value="graph" aria-label="Graph view">
            <BarChartOutlinedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <TableChartOutlinedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {viewMode === "graph" ? (
        // A ranked bar list drawn as a borderless table: the name column sizes
        // itself to the longest store, the bar takes the rest of the row, and the
        // value sits in its own column so it can never clip.
        <TableContainer sx={{ px: "16px", pb: "16px" }}>
          <Table size="small" aria-label="Gross sales by store">
            <TableBody>
              {sortedByGross.map((item, rowIndex) => {
                const gross = Number(item.gross_total || 0);
                const share = maxGross > 0 ? (gross / maxGross) * 100 : 0;
                return (
                  <Tooltip
                    key={item.store_id ?? rowIndex}
                    title={<StoreMetrics item={item} />}
                    placement="top"
                    enterDelay={150}
                    componentsProps={{ tooltip: { sx: tooltipSx } }}
                  >
                    <TableRow
                      tabIndex={0}
                      sx={{
                        "& td": { border: 0, py: "6px" },
                        "&:hover, &:focus-visible": {
                          backgroundColor: "action.hover",
                          outline: "none",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          width: "1%",
                          maxWidth: 220,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          pl: "8px",
                          pr: "12px",
                        }}
                      >
                        <Typography variant="body2" noWrap>
                          {item.store_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: "100%", px: 0 }}>
                        <Box
                          sx={{
                            height: "18px",
                            borderRadius: "0 4px 4px 0",
                            backgroundColor: "neutral.100",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${share}%`,
                              minWidth: gross > 0 ? "2px" : 0,
                              height: "100%",
                              borderRadius: "0 4px 4px 0",
                              backgroundColor: chartColors.gross,
                              transition: "width 300ms ease",
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", pl: "12px", pr: "8px" }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatAmount(gross)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </Tooltip>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer
          sx={{
            px: "24px",
            pb: "24px",
          }}
        >
          <Table
            size="small"
            sx={{
              width: "100%",
              border: "1px solid #E6E8F0",
              borderRadius: "8px",
              overflow: "hidden",
              "& .MuiTableCell-root": {
                borderBottom: "1px solid #E6E8F0",
              },
              "& .MuiTableCell-root:not(:last-child)": {
                borderRight: "1px solid #E6E8F0",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === "store_name"}
                    direction={
                      sortConfig.key === "store_name"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => handleSort("store_name")}
                    sx={sortLabelSx}
                  >
                    Store Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortConfig.key === "gross_total"}
                    direction={
                      sortConfig.key === "gross_total"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => handleSort("gross_total")}
                    sx={sortLabelSx}
                  >
                    Gross Sales
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortConfig.key === "net_total"}
                    direction={
                      sortConfig.key === "net_total" ? sortConfig.direction : "asc"
                    }
                    onClick={() => handleSort("net_total")}
                    sx={sortLabelSx}
                  >
                    Net Sales
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortConfig.key === "atv"}
                    direction={
                      sortConfig.key === "atv" ? sortConfig.direction : "asc"
                    }
                    onClick={() => handleSort("atv")}
                    sx={sortLabelSx}
                  >
                    ATV
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortConfig.key === "transaction_count"}
                    direction={
                      sortConfig.key === "transaction_count"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => handleSort("transaction_count")}
                    sx={sortLabelSx}
                  >
                    Transactions
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTableData.map((item, rowIndex) => (
                <TableRow
                  key={item.store_id ?? rowIndex}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(11, 87, 208, 0.06)",
                    },
                  }}
                >
                  <TableCell>{item.store_name}</TableCell>
                  <TableCell align="right">
                    {formatAmount(item.gross_total)}
                  </TableCell>
                  <TableCell align="right">
                    {formatAmount(item.net_total)}
                  </TableCell>
                  <TableCell align="right">{formatAmount(item.atv)}</TableCell>
                  <TableCell align="right">
                    {Number(item.transaction_count || 0).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DashboardChartCard>
  );
};
